import React from 'react';
import { Link } from 'react-router-dom';
import { FixLane, FixResponse } from '../../types/fix';
import { getRepositoryShortName } from '../../utils/repository';
import { Spinner } from '../../design-system';
import { useNavigationOrigin } from '../../hooks/useNavigationOrigin';
import { diffStat, fixCardMeta } from './lanes';
import DiffStatBadge from './DiffStatBadge';

export interface FixBoardCardProps {
  fix: FixResponse;
  lane: FixLane;
  applicationId: string;
  /** Show which repository the fix targets (legacy apps hold more than one). */
  showRepo?: boolean;
}

/**
 * One fix on the board: its title, and a single line saying where it is in the pipeline
 * ("8 min · 3 files", "PR #142 · Aug 11"). The whole card links to the fix detail page.
 */
const FixBoardCard: React.FC<FixBoardCardProps> = ({ fix, lane, applicationId, showRepo = false }) => {
  const working = lane === 'in_progress' || (lane === 'ready' && !!fix.pr_creation_pending);
  const repo = showRepo && fix.source_repo ? getRepositoryShortName(fix.source_repo) : null;
  const stat = diffStat(fix);
  const origin = useNavigationOrigin('Fixes');

  return (
    <Link
      to={`/repositories/${applicationId}/fixes/${fix.id}`}
      // So the fix page's back button returns to the board, not to Findings.
      state={origin}
      className="transition-colors hover:border-[var(--border-strong)]"
      style={{
        display: 'block',
        padding: '12px 14px',
        background: 'var(--surface-1)',
        border: `1px solid ${working ? 'var(--lime-soft-border)' : 'var(--border-subtle)'}`,
        borderRadius: 'var(--radius-lg)',
        textDecoration: 'none',
      }}
    >
      <span
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          fontSize: 13.5,
          fontWeight: 600,
          lineHeight: 1.35,
          color: 'var(--text-primary)',
        }}
      >
        {fix.title}
      </span>

      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginTop: 7,
          fontSize: 11.5,
          color: 'var(--text-muted)',
        }}
      >
        {working && <Spinner size={11} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {fixCardMeta(fix, lane)}
        </span>
        {stat && (
          <>
            <span aria-hidden="true">·</span>
            <DiffStatBadge fix={fix} />
          </>
        )}
      </span>

      {repo && (
        <span
          style={{
            display: 'block',
            marginTop: 5,
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-faint)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {repo}
        </span>
      )}
    </Link>
  );
};

export default FixBoardCard;
